import React, { useState, useCallback } from 'react';
import {
  Calendar,
  Search,
  CheckCircle,
  AlertTriangle,
  Loader2,
  CreditCard,
  Zap,
  ChevronDown,
  ChevronUp,
  X,
  Link,
  DollarSign,
  User,
  Trash2,
  Building2,
  Layers,
  Printer,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import toast, { Toaster } from 'react-hot-toast';
import apiService from '../services/apiService';
import AjudaConciliacao from '../components/AjudaConciliacao';
import SituacaoBaixaErp from '../components/SituacaoBaixaErp';

// ─── helpers ──────────────────────────────────────────────────────────────────
const moeda = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const hoje = () => format(new Date(), 'yyyy-MM-dd');

const TIPO_LABELS = {
  pix:            { label: 'PIX',            cor: 'bg-purple-100 text-purple-800', Icon: Zap },
  cartao_credito: { label: 'Cartão Crédito', cor: 'bg-orange-100 text-orange-800', Icon: CreditCard },
  cartao_debito:  { label: 'Cartão Débito',  cor: 'bg-green-100 text-green-800',   Icon: CreditCard },
};

// ─── agrupamento para impressão ───────────────────────────────────────────────
function agruparParaImpressao(lancamentos) {
  // Mapeia link_pix_id → set de índices de lancamentos que o utilizam
  const pixToIdxs = {};
  lancamentos.forEach((lanc, idx) => {
    lanc.conciliacoes.forEach((conc) => {
      if (conc.tipo_pagamento === 'pix' && conc.link_pix_id) {
        if (!pixToIdxs[conc.link_pix_id]) pixToIdxs[conc.link_pix_id] = new Set();
        pixToIdxs[conc.link_pix_id].add(idx);
      }
    });
  });

  const inGrupo = new Set();
  const grupos = [];

  // Grupos N:1 — múltiplos lancamentos compartilham o mesmo PIX
  Object.entries(pixToIdxs).forEach(([pixId, idxSet]) => {
    if (idxSet.size > 1) {
      const idxs = [...idxSet];
      const grpLancs = idxs.map((i) => lancamentos[i]);
      const concPix = grpLancs[0].conciliacoes.find((c) => String(c.link_pix_id) === pixId);
      grupos.push({
        tipo: 'grupo',
        lancamentos: grpLancs,
        pixId: Number(pixId),
        concPix,
        total: grpLancs.reduce((s, l) => s + l.valor_outros, 0),
      });
      idxs.forEach((i) => inGrupo.add(i));
    }
  });

  // Lancamentos individuais (não pertencentes a nenhum grupo N:1)
  lancamentos.forEach((lanc, idx) => {
    if (!inGrupo.has(idx)) grupos.push({ tipo: 'individual', lanc });
  });

  return grupos;
}

// ─── badge tipo ───────────────────────────────────────────────────────────────
function TipoBadge({ tipo }) {
  const cfg = TIPO_LABELS[tipo];
  if (!cfg) return null;
  const { label, cor, Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cor}`}>
      <Icon className="w-3 h-3" />
      {label}
    </span>
  );
}

// ─── modal: escolher tipo de pagamento ────────────────────────────────────────
function ModalEscolhaTipo({ lancamento, onClose, onEscolher }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
        <div className="p-5 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-900">Como foi recebido?</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-gray-500" /></button>
        </div>
        <div className="p-5 text-sm text-gray-600 mb-1">
          <span className="font-medium text-gray-800">{lancamento.descricao || '(sem descrição)'}</span>
          {' — '}{moeda(lancamento.valor_outros)}
        </div>
        <div className="p-5 grid grid-cols-2 gap-3">
          {Object.entries(TIPO_LABELS).map(([tipo, { label, Icon, cor }]) => (
            <button
              key={tipo}
              onClick={() => onEscolher(tipo)}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border-2 border-transparent hover:border-purple-400 hover:bg-purple-50 transition-colors"
            >
              <Icon className="w-8 h-8 text-gray-600" />
              <span className="text-sm font-medium text-gray-800 text-center">{label}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── lista de PIX compartilhada (usada em ModalPix e ModalPixGrupo) ───────────
function PixSearchPanel({ dataCaixa, selecionado, onSelecionado }) {
  const [busca, setBusca] = useState('');
  const [pixList, setPixList] = useState([]);
  const [loading, setLoading] = useState(false);

  const buscarPix = async () => {
    setLoading(true);
    try {
      const resp = await apiService.get('/api/conciliacao/caixa/pix-conciliados', {
        params: { busca, data: dataCaixa },
      });
      setPixList(resp.data?.data || []);
    } catch {
      toast.error('Erro ao buscar PIX');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && buscarPix()}
          placeholder="Buscar por nome do cliente..."
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        />
        <button
          onClick={buscarPix}
          disabled={loading}
          className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Buscar
        </button>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          <span className="ml-2 text-sm text-gray-500">Buscando PIX conciliados...</span>
        </div>
      )}

      {!loading && pixList.length === 0 && busca && (
        <p className="text-sm text-gray-500 text-center py-4">
          Nenhum PIX conciliado encontrado. Verifique a página Conciliação PIX.
        </p>
      )}

      {pixList.length > 0 && (
        <div className="border border-gray-200 rounded-lg divide-y max-h-56 overflow-y-auto">
          {pixList.map((pix) => (
            <button
              key={pix.id}
              onClick={() => onSelecionado(pix)}
              className={`w-full text-left px-4 py-3 hover:bg-purple-50 transition-colors ${selecionado?.id === pix.id ? 'bg-purple-50 border-l-4 border-purple-500' : ''}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 text-sm flex items-center gap-1.5">
                    {pix.nome_cliente}
                    {pix.origem_pix === 'pix_bancario' && (
                      <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                        <Building2 className="w-3 h-3" />Pix Bancário
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-500">
                    {pix.data_pagamento ? format(new Date(pix.data_pagamento), 'dd/MM/yyyy HH:mm') : ''}
                    {pix.pagador_nome && pix.pagador_nome !== pix.nome_cliente
                      ? ` · Pago por: ${pix.pagador_nome}` : ''}
                    {' · PIX #'}{pix.id}
                  </p>
                </div>
                <span className="font-semibold text-purple-700">{moeda(pix.valor_pago || pix.valor)}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── modal: PIX individual (com campo valor parcial para 1:N) ─────────────────
function ModalPix({ lancamento, dataCaixa, onClose, onSalvar }) {
  const valorRestante = lancamento.valor_outros - (lancamento.total_conciliado || 0);
  const [pixSelecionado, setPixSelecionado] = useState(null);
  const [valorParcial, setValorParcial] = useState(String(valorRestante.toFixed(2)));
  const [observacoes, setObservacoes] = useState('');

  const temConciliacaoParcial = (lancamento.total_conciliado || 0) > 0;
  const valorParcialNum = parseFloat(valorParcial) || 0;
  const valorValido = valorParcialNum > 0 && valorParcialNum <= valorRestante + 0.01;

  const salvar = () => {
    if (!pixSelecionado) { toast.error('Selecione um PIX conciliado'); return; }
    if (!valorValido) { toast.error(`Valor deve ser entre R$ 0,01 e ${moeda(valorRestante)}`); return; }
    onSalvar({
      tipo_pagamento: 'pix',
      link_pix_id: pixSelecionado.id,
      cliente_nome: pixSelecionado.nome_cliente,
      observacoes,
      valor_parcial: valorParcialNum,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b bg-gradient-to-r from-purple-600 to-purple-800 rounded-t-xl flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white text-lg">PIX Conciliado</h3>
            <p className="text-purple-200 text-sm mt-0.5">
              {lancamento.descricao} — {moeda(lancamento.valor_outros)}
              {temConciliacaoParcial && ` · Restante: ${moeda(valorRestante)}`}
            </p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-white" /></button>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-gray-600">
            Selecione o PIX já conciliado correspondente a este lançamento.
          </p>

          <PixSearchPanel
            dataCaixa={dataCaixa}
            selecionado={pixSelecionado}
            onSelecionado={setPixSelecionado}
          />

          {pixSelecionado && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-purple-900">
                ✅ Selecionado: {pixSelecionado.nome_cliente} — {moeda(pixSelecionado.valor_pago || pixSelecionado.valor)}
              </p>
            </div>
          )}

          {/* Valor parcial — sempre visível para permitir splits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valor deste PIX para este recebimento (R$)
              {temConciliacaoParcial && (
                <span className="ml-2 text-xs font-normal text-yellow-600">
                  (restam {moeda(valorRestante)})
                </span>
              )}
            </label>
            <input
              type="number"
              step="0.01"
              min="0.01"
              max={valorRestante}
              value={valorParcial}
              onChange={(e) => setValorParcial(e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                valorValido ? 'border-gray-300' : 'border-red-400'
              }`}
            />
            {!valorValido && valorParcial && (
              <p className="text-xs text-red-500 mt-1">
                Valor deve ser entre R$ 0,01 e {moeda(valorRestante)}
              </p>
            )}
          </div>

          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={2}
            placeholder="Observações (opcional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700">
              Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={!pixSelecionado || !valorValido}
              className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
            >
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── modal: PIX grupo (N:1 — vários recebimentos → mesmo PIX) ─────────────────
function ModalPixGrupo({ lancamentos, dataCaixa, onClose, onSalvar }) {
  const [pixSelecionado, setPixSelecionado] = useState(null);
  const [observacoes, setObservacoes] = useState('');
  const [salvando, setSalvando] = useState(false);

  const totalSelecionados = lancamentos.reduce((s, l) => s + l.valor_outros, 0);
  const valorPix = pixSelecionado ? (pixSelecionado.valor_pago || pixSelecionado.valor) : null;
  const diferenca = valorPix !== null ? Math.abs(totalSelecionados - valorPix) : null;
  const valoresOk = diferenca !== null && diferenca < 0.02;

  const salvar = async () => {
    if (!pixSelecionado) { toast.error('Selecione um PIX conciliado'); return; }
    if (!valoresOk) {
      toast.error(`Diferença de ${moeda(diferenca)} entre a soma dos recebimentos e o PIX`);
      return;
    }
    setSalvando(true);
    try {
      await onSalvar({ link_pix_id: pixSelecionado.id, cliente_nome: pixSelecionado.nome_cliente, observacoes });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-t-xl flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white text-lg flex items-center gap-2">
              <Layers className="w-5 h-5" />
              Vincular {lancamentos.length} recebimentos ao mesmo PIX
            </h3>
            <p className="text-indigo-200 text-sm mt-0.5">Total: {moeda(totalSelecionados)}</p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-white" /></button>
        </div>

        <div className="p-5 space-y-4">
          {/* Lista dos recebimentos selecionados */}
          <div className="border border-gray-200 rounded-lg divide-y bg-gray-50">
            {lancamentos.map((l, i) => (
              <div key={i} className="flex items-center justify-between px-3 py-2 text-sm">
                <span className="text-gray-700 truncate">{l.descricao}</span>
                <span className="font-medium text-gray-900 ml-4 flex-shrink-0">{moeda(l.valor_outros)}</span>
              </div>
            ))}
            <div className="flex items-center justify-between px-3 py-2 text-sm font-semibold bg-gray-100 rounded-b-lg">
              <span>Total dos recebimentos</span>
              <span>{moeda(totalSelecionados)}</span>
            </div>
          </div>

          <p className="text-sm text-gray-600">
            Selecione o PIX que corresponde ao pagamento conjunto destes recebimentos.
          </p>

          <PixSearchPanel
            dataCaixa={dataCaixa}
            selecionado={pixSelecionado}
            onSelecionado={setPixSelecionado}
          />

          {/* Validação de valores */}
          {pixSelecionado && (
            <div className={`rounded-lg p-3 text-sm border ${valoresOk ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              {valoresOk ? (
                <p className="text-green-800 font-medium">
                  ✅ PIX de {moeda(valorPix)} corresponde à soma dos recebimentos ({moeda(totalSelecionados)})
                </p>
              ) : (
                <p className="text-red-800 font-medium">
                  ⚠️ Diferença de {moeda(diferenca)}: soma dos recebimentos ({moeda(totalSelecionados)}) ≠ PIX ({moeda(valorPix)})
                </p>
              )}
            </div>
          )}

          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={2}
            placeholder="Observações (opcional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
          />

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700">
              Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={!pixSelecionado || !valoresOk || salvando}
              className="flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
              Salvar {lancamentos.length} vínculos
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── modal: cartão ────────────────────────────────────────────────────────────
function ModalCartao({ lancamento, tipo, onClose, onSalvar }) {
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [loadingBusca, setLoadingBusca] = useState(false);
  const [parcelas, setParcelas] = useState(1);
  const [valorTotal, setValorTotal] = useState(String(lancamento.valor_outros || ''));
  const [observacoes, setObservacoes] = useState('');

  const buscarCliente = async () => {
    if (!buscaCliente.trim()) return;
    setLoadingBusca(true);
    try {
      const resp = await apiService.clientes.buscar(buscaCliente, 10, 0);
      const list = Array.isArray(resp.data) ? resp.data : (resp.data?.data || []);
      setClientes(list.slice(0, 10));
    } catch {
      toast.error('Erro ao buscar cliente');
    } finally {
      setLoadingBusca(false);
    }
  };

  const salvar = () => {
    if (!clienteSel) { toast.error('Selecione o cliente'); return; }
    if (!valorTotal || isNaN(Number(valorTotal))) { toast.error('Informe o valor total'); return; }
    onSalvar({
      tipo_pagamento: tipo,
      cliente_ordem: clienteSel.Ordem,
      cliente_codigo: clienteSel.Codigo,
      cliente_nome: clienteSel.Nome,
      cartao_tipo: tipo === 'cartao_credito' ? 'credito' : 'debito',
      cartao_parcelas: tipo === 'cartao_credito' ? parseInt(parcelas) : 1,
      cartao_valor_total: Number(valorTotal),
      observacoes,
    });
  };

  const titulo = tipo === 'cartao_credito' ? 'Cartão de Crédito' : 'Cartão de Débito';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b bg-gradient-to-r from-orange-500 to-orange-700 rounded-t-xl flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-white text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5" /> {titulo}
            </h3>
            <p className="text-orange-100 text-sm mt-0.5">
              {lancamento.descricao} — {moeda(lancamento.valor_outros)}
            </p>
          </div>
          <button onClick={onClose}><X className="w-5 h-5 text-white" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cliente (conforme ERP)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={buscaCliente}
                onChange={(e) => setBuscaCliente(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && buscarCliente()}
                placeholder="Nome ou código..."
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
              <button
                onClick={buscarCliente}
                disabled={loadingBusca}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 disabled:opacity-50 flex items-center gap-1"
              >
                {loadingBusca ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </button>
            </div>

            {clientes.length > 0 && !clienteSel && (
              <div className="mt-2 border border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto">
                {clientes.map((c) => (
                  <button
                    key={c.Ordem}
                    onClick={() => { setClienteSel(c); setClientes([]); }}
                    className="w-full text-left px-3 py-2 hover:bg-orange-50 text-sm"
                  >
                    <span className="font-medium text-gray-900">{c.Nome}</span>
                    <span className="text-gray-500 ml-2">#{c.Codigo}</span>
                  </button>
                ))}
              </div>
            )}

            {clienteSel && (
              <div className="mt-2 bg-orange-50 border border-orange-200 rounded-lg p-2 flex items-center justify-between text-sm">
                <span className="font-medium text-orange-900"><User className="w-3 h-3 inline mr-1" />{clienteSel.Nome}</span>
                <button onClick={() => setClienteSel(null)} className="text-orange-600 hover:text-orange-800">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor Total (R$)</label>
            <input
              type="number"
              step="0.01"
              value={valorTotal}
              onChange={(e) => setValorTotal(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {tipo === 'cartao_credito' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Número de Parcelas</label>
              <select
                value={parcelas}
                onChange={(e) => setParcelas(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              >
                {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                  <option key={n} value={n}>{n}x {n > 1 ? `de ${moeda(Number(valorTotal) / n)}` : '(à vista)'}</option>
                ))}
              </select>
            </div>
          )}

          <textarea
            value={observacoes}
            onChange={(e) => setObservacoes(e.target.value)}
            rows={2}
            placeholder="Observações (opcional)"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none"
          />

          <div className="flex gap-3">
            <button onClick={onClose} className="flex-1 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-700">
              Cancelar
            </button>
            <button onClick={salvar} className="flex-1 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium">
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── linha de lançamento ──────────────────────────────────────────────────────
function LinhaLancamento({ lanc, dataCaixa, onRefresh, selecionado, onToggleSelect }) {
  const [expandido, setExpandido] = useState(false);
  const [modalTipo, setModalTipo] = useState(false);
  const [modalPix, setModalPix] = useState(false);
  const [modalCartao, setModalCartao] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const abrirModal = (tipo) => {
    setModalTipo(false);
    if (tipo === 'pix') setModalPix(true);
    else setModalCartao(tipo);
  };

  const salvar = async (dados) => {
    setSalvando(true);
    try {
      await apiService.post('/api/conciliacao/caixa/vincular', {
        data_caixa: dataCaixa,
        descricao_caixa: lanc.descricao,
        tipo_movimento: lanc.tipo_movimento,
        tipo_movimento_origem: lanc.tipo_movimento_origem,
        valor: dados.valor_parcial ?? lanc.valor_outros,
        ...dados,
      });
      toast.success('Conciliação salva!');
      setModalPix(false);
      setModalCartao(null);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (id) => {
    if (!confirm('Remover esta conciliação?')) return;
    try {
      await apiService.delete(`/api/conciliacao/caixa/${id}`);
      toast.success('Removido');
      onRefresh();
    } catch {
      toast.error('Erro ao remover');
    }
  };

  const totalPendente = lanc.valor_outros - lanc.total_conciliado;
  const conciliado = lanc.conciliado;
  const podeSelecionarMais = !conciliado;

  return (
    <>
      <div className={`bg-white rounded-lg border shadow-sm transition-colors ${
        conciliado ? 'border-green-200' : selecionado ? 'border-indigo-400 ring-2 ring-indigo-200' : 'border-gray-200'
      }`}>
        <div className="flex items-center gap-3 p-4">
          {/* checkbox de seleção (apenas para não conciliados) */}
          <div className="flex-shrink-0 w-8 flex items-center justify-center">
            {podeSelecionarMais ? (
              <input
                type="checkbox"
                checked={selecionado}
                onChange={onToggleSelect}
                className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
              />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
          </div>

          {/* ícone de status (apenas para não conciliados) */}
          {podeSelecionarMais && (
            <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-yellow-100">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
            </div>
          )}

          {/* descrição */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 truncate">{lanc.descricao || '(sem descrição)'}</p>
            <p className="text-xs text-gray-500">
              {lanc.tipo_movimento} · {lanc.operador && `Operador: ${lanc.operador}`}
            </p>
            {lanc.conciliacoes.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {lanc.conciliacoes.map((c) => (
                  <TipoBadge key={c.id} tipo={c.tipo_pagamento} />
                ))}
              </div>
            )}
          </div>

          {/* valores */}
          <div className="text-right flex-shrink-0 mr-2">
            <p className="font-bold text-gray-900">{moeda(lanc.valor_outros)}</p>
            {!conciliado && lanc.total_conciliado > 0 && (
              <p className="text-xs text-yellow-600">Falta: {moeda(totalPendente)}</p>
            )}
          </div>

          {/* ações */}
          <div className="flex gap-2 flex-shrink-0">
            {!conciliado && (
              <button
                onClick={() => setModalTipo(true)}
                disabled={salvando}
                className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg flex items-center gap-1.5 disabled:opacity-50"
              >
                <Link className="w-3.5 h-3.5" />
                Vincular
              </button>
            )}
            {lanc.conciliacoes.length > 0 && (
              <button
                onClick={() => setExpandido(!expandido)}
                className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                {expandido ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            )}
          </div>
        </div>

        {/* detalhes das conciliações */}
        {expandido && lanc.conciliacoes.length > 0 && (
          <div className="border-t border-gray-100 px-4 pb-3 pt-2 space-y-2">
            {lanc.conciliacoes.map((c) => (
              <div key={c.id} className="flex items-center gap-3 text-sm bg-gray-50 rounded-lg px-3 py-2">
                <TipoBadge tipo={c.tipo_pagamento} />
                <span className="flex-1 text-gray-700">
                  {c.cliente_nome || c.observacoes || '—'}
                  {c.cartao_parcelas > 1 ? ` · ${c.cartao_parcelas}x` : ''}
                  {c.link_pix_id && <span className="block mt-1"><SituacaoBaixaErp dados={c.baixa_erp} /></span>}
                </span>
                <span className="font-medium text-gray-900">{moeda(c.valor)}</span>
                <button onClick={() => remover(c.id)} className="text-red-400 hover:text-red-600 ml-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalTipo && (
        <ModalEscolhaTipo lancamento={lanc} onClose={() => setModalTipo(false)} onEscolher={abrirModal} />
      )}
      {modalPix && (
        <ModalPix
          lancamento={lanc}
          dataCaixa={dataCaixa}
          onClose={() => setModalPix(false)}
          onSalvar={salvar}
        />
      )}
      {modalCartao && (
        <ModalCartao
          lancamento={lanc}
          tipo={modalCartao}
          onClose={() => setModalCartao(null)}
          onSalvar={salvar}
        />
      )}
    </>
  );
}

// ─── barra flutuante de seleção múltipla ──────────────────────────────────────
function BarraSelecaoMultipla({ selecionados, onVincular, onLimpar }) {
  const total = selecionados.reduce((s, l) => s + l.valor_outros, 0);

  if (selecionados.length < 2) return null;

  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
      <div className="bg-indigo-700 text-white rounded-xl shadow-2xl px-5 py-3 flex items-center gap-4 min-w-max">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-indigo-300" />
          <span className="text-sm font-medium">
            {selecionados.length} selecionados
          </span>
          <span className="text-indigo-300">·</span>
          <span className="text-sm font-bold">{moeda(total)}</span>
        </div>
        <button
          onClick={onVincular}
          className="px-4 py-1.5 bg-white text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-50 flex items-center gap-1.5"
        >
          <Link className="w-3.5 h-3.5" />
          Vincular ao mesmo PIX
        </button>
        <button
          onClick={onLimpar}
          className="p-1 text-indigo-300 hover:text-white"
          title="Limpar seleção"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

// ─── página principal ─────────────────────────────────────────────────────────
export default function ConciliacaoCaixa() {
  const [data, setData] = useState(hoje());
  const [lancamentos, setLancamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buscado, setBuscado] = useState(false);
  const [selecionados, setSelecionados] = useState([]);
  const [modalPixGrupo, setModalPixGrupo] = useState(false);

  const buscar = useCallback(async () => {
    if (!data) { toast.error('Selecione uma data'); return; }
    setLoading(true);
    setBuscado(false);
    setSelecionados([]);
    try {
      const resp = await apiService.get('/api/conciliacao/caixa/lancamentos', { params: { data } });
      setLancamentos(resp.data?.data || []);
      setBuscado(true);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao buscar lançamentos');
    } finally {
      setLoading(false);
    }
  }, [data]);

  const toggleSelecionado = useCallback((lanc) => {
    setSelecionados((prev) => {
      const idx = prev.findIndex((l) => l._chave === lanc._chave);
      if (idx >= 0) return prev.filter((_, i) => i !== idx);
      return [...prev, lanc];
    });
  }, []);

  const salvarGrupo = async ({ link_pix_id, cliente_nome, observacoes }) => {
    try {
      for (const lanc of selecionados) {
        await apiService.post('/api/conciliacao/caixa/vincular', {
          data_caixa: data,
          descricao_caixa: lanc.descricao,
          tipo_movimento: lanc.tipo_movimento,
          tipo_movimento_origem: lanc.tipo_movimento_origem,
          valor: lanc.valor_outros,
          tipo_pagamento: 'pix',
          link_pix_id,
          cliente_nome,
          observacoes,
        });
      }
      toast.success(`${selecionados.length} conciliações salvas!`);
      setSelecionados([]);
      setModalPixGrupo(false);
      buscar();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar grupo');
      throw err;
    }
  };

  const conciliados = lancamentos.filter((l) => l.conciliado).length;
  const pendentes = lancamentos.length - conciliados;
  const totalPendente = lancamentos
    .filter((l) => !l.conciliado)
    .reduce((s, l) => s + (l.valor_outros - l.total_conciliado), 0);

  const imprimir = () => {
    const dataFmt = format(new Date(data + 'T12:00:00'), "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
    const grupos = agruparParaImpressao(lancamentos);

    const tipoBadgeHtml = (tipo) => {
      if (tipo === 'pix')
        return `<span style="background:#ede9fe;color:#5b21b6;padding:1px 6px;border-radius:8px;font-size:9px;font-weight:600">⚡ PIX</span>`;
      if (tipo === 'cartao_credito')
        return `<span style="background:#ffedd5;color:#c2410c;padding:1px 6px;border-radius:8px;font-size:9px;font-weight:600">💳 Crédito</span>`;
      if (tipo === 'cartao_debito')
        return `<span style="background:#dcfce7;color:#15803d;padding:1px 6px;border-radius:8px;font-size:9px;font-weight:600">💳 Débito</span>`;
      return `<span>${tipo}</span>`;
    };

    const renderConc = (conc) => {
      const extra = conc.cartao_parcelas > 1 ? ` · ${conc.cartao_parcelas}x` : '';
      const classe = conc.tipo_pagamento === 'pix' ? 'sub-pix' : 'sub-cartao';
      return `<div class="sub-item ${classe}">
        ${tipoBadgeHtml(conc.tipo_pagamento)}
        <span class="sub-nome">${conc.cliente_nome || conc.observacoes || '—'}${extra}</span>
        ${conc.link_pix_id ? `<span class="sub-id">PIX #${conc.link_pix_id}</span>` : ''}
        <span class="sub-valor">${moeda(conc.valor)}</span>
      </div>`;
    };

    let corpo = '';
    grupos.forEach((grupo) => {
      if (grupo.tipo === 'grupo') {
        corpo += `<div class="card grupo-card">`;
        grupo.lancamentos.forEach((lanc) => {
          corpo += `<div class="lanc-row">
            <span class="lanc-desc">${lanc.descricao || '(sem descrição)'}</span>
            <span class="badge-ok">✓</span>
            <span class="lanc-info">${lanc.tipo_movimento} · ${lanc.operador || '—'}</span>
            <span class="lanc-valor">${moeda(lanc.valor_outros)}</span>
          </div>`;
        });
        corpo += `<div class="grupo-total">
          <span>Subtotal — ${grupo.lancamentos.length} recebimento(s)</span>
          <span>${moeda(grupo.total)}</span>
        </div>`;
        if (grupo.concPix) corpo += renderConc(grupo.concPix);
        corpo += `</div>`;
      } else {
        const { lanc } = grupo;
        corpo += `<div class="card ${lanc.conciliado ? '' : 'card-pend'}">
          <div class="lanc-row">
            <span class="lanc-desc">${lanc.descricao || '(sem descrição)'}</span>
            <span class="${lanc.conciliado ? 'badge-ok' : 'badge-pend'}">${lanc.conciliado ? '✓ Conciliado' : '⚠ Pendente'}</span>
            <span class="lanc-info">${lanc.tipo_movimento} · ${lanc.operador || '—'}</span>
            <span class="lanc-valor">${moeda(lanc.valor_outros)}</span>
          </div>
          ${lanc.conciliacoes.map(renderConc).join('')}
        </div>`;
      }
    });

    const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
<meta charset="UTF-8">
<title>Conciliação de Caixa — ${dataFmt}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:20px}
h1{font-size:15px;font-weight:700}
.sub{color:#555;font-size:11px;margin-bottom:14px}
.resumo{display:flex;gap:20px;padding:8px 12px;background:#f3f4f6;border-radius:6px;margin-bottom:16px}
.res-item strong{display:block;font-size:16px}
.res-item small{color:#6b7280;font-size:9px}
hr{border:none;border-top:1px solid #e5e7eb;margin:12px 0}
.card{border:1px solid #e5e7eb;border-radius:6px;padding:8px 10px;margin-bottom:8px;page-break-inside:avoid}
.card-pend{border-color:#fcd34d;background:#fffbeb}
.grupo-card{border-color:#a5b4fc;background:#f5f3ff}
.lanc-row{display:flex;align-items:baseline;gap:6px;margin-bottom:2px}
.lanc-desc{font-weight:700;font-size:11px;flex:1}
.lanc-info{font-size:9px;color:#6b7280}
.lanc-valor{font-weight:700;white-space:nowrap;margin-left:auto}
.badge-ok{font-size:9px;background:#d1fae5;color:#065f46;padding:0 5px;border-radius:8px;white-space:nowrap}
.badge-pend{font-size:9px;background:#fef3c7;color:#92400e;padding:0 5px;border-radius:8px;white-space:nowrap}
.grupo-total{display:flex;justify-content:space-between;font-size:10px;font-weight:600;padding:4px 0;margin:4px 0;border-top:1px dashed #c4b5fd;border-bottom:1px dashed #c4b5fd;color:#4338ca}
.sub-item{display:flex;align-items:center;gap:6px;margin-top:5px;padding:4px 8px 4px 18px;font-size:10px;border-radius:0 4px 4px 0}
.sub-pix{background:#f5f3ff;border-left:3px solid #7c3aed}
.sub-cartao{background:#fff7ed;border-left:3px solid #ea580c}
.sub-nome{flex:1}
.sub-id{color:#7c3aed;font-size:9px}
.sub-valor{font-weight:700;margin-left:auto;white-space:nowrap}
@media print{body{padding:10mm}}
</style>
</head>
<body>
<h1>Conciliação de Caixa</h1>
<p class="sub">Data: ${dataFmt} &nbsp;·&nbsp; Emitido em: ${format(new Date(), 'dd/MM/yyyy HH:mm', { locale: ptBR })}</p>
<div class="resumo">
  <div class="res-item"><strong style="color:#4338ca">${lancamentos.length}</strong><small>Lançamentos</small></div>
  <div class="res-item"><strong style="color:#16a34a">${conciliados}</strong><small>Conciliados</small></div>
  <div class="res-item"><strong style="color:#d97706">${pendentes}</strong><small>Pendentes</small></div>
  <div class="res-item"><strong style="color:#d97706">${moeda(totalPendente)}</strong><small>Valor Pendente</small></div>
</div>
<hr>
${corpo}
</body>
</html>`;

    const win = window.open('', '_blank', 'width=860,height=700');
    win.document.write(html);
    win.document.close();
    win.onload = () => win.print();
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />

      {/* cabeçalho */}
      <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 rounded-lg shadow-lg p-6 text-white">
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <DollarSign className="w-7 h-7" />
          Conciliação de Caixa
          <AjudaConciliacao />
        </h1>
        <p className="text-indigo-200 mt-1 text-sm">
          Vincule os recebimentos do caixa ao tipo de pagamento correspondente
        </p>
      </div>

      {/* seleção de data */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-gray-500" />
          <label className="text-sm font-medium text-gray-700">Data do caixa:</label>
        </div>
        <input
          type="date"
          value={data}
          onChange={(e) => setData(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
        <button
          onClick={buscar}
          disabled={loading}
          className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Consultar
        </button>
        <button
          onClick={imprimir}
          disabled={!buscado || lancamentos.length === 0}
          className="ml-auto px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Imprimir conciliações do dia"
        >
          <Printer className="w-4 h-4" />
          Imprimir
        </button>
      </div>

      {/* resumo */}
      {buscado && lancamentos.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <p className="text-2xl font-bold text-indigo-700">{lancamentos.length}</p>
            <p className="text-xs text-gray-500 mt-1">Total de Lançamentos</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{conciliados}</p>
            <p className="text-xs text-gray-500 mt-1">Conciliados</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4 text-center">
            <p className="text-2xl font-bold text-yellow-600">{pendentes}</p>
            <p className="text-xs text-gray-500 mt-1">Pendentes — {moeda(totalPendente)}</p>
          </div>
        </div>
      )}

      {/* lista — com padding-bottom extra quando barra flutuante está visível */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
          <span className="ml-3 text-gray-600">Consultando caixa...</span>
        </div>
      ) : buscado && lancamentos.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-500">
          <DollarSign className="w-14 h-14 mx-auto mb-3 text-gray-300" />
          <p>Nenhum lançamento encontrado para {format(new Date(data + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR })}</p>
        </div>
      ) : (
        <div className={`space-y-3 ${selecionados.length >= 2 ? 'pb-24' : ''}`}>
          {lancamentos.map((lanc, idx) => (
            <LinhaLancamento
              key={`${lanc.descricao}-${lanc.tipo_movimento}-${idx}`}
              lanc={lanc}
              dataCaixa={data}
              onRefresh={buscar}
              selecionado={selecionados.some((s) => s._chave === lanc._chave)}
              onToggleSelect={() => toggleSelecionado(lanc)}
            />
          ))}
        </div>
      )}

      {/* legenda */}
      {buscado && lancamentos.length > 0 && (
        <div className="bg-white rounded-lg shadow p-4">
          <p className="text-xs font-medium text-gray-700 mb-3">Legenda — Tipos de Pagamento:</p>
          <div className="flex flex-wrap gap-3">
            {Object.entries(TIPO_LABELS).map(([tipo, cfg]) => (
              <div key={tipo} className="flex items-center gap-1.5">
                <TipoBadge tipo={tipo} />
                <span className="text-xs text-gray-600">
                  {tipo === 'pix' && '— PIX conciliado (sistema ou bancário)'}
                  {tipo === 'cartao_credito' && '— Cartão de crédito'}
                  {tipo === 'cartao_debito' && '— Cartão de débito'}
                </span>
              </div>
            ))}
          </div>
          {buscado && (
            <p className="text-xs text-gray-400 mt-3">
              Dica: selecione múltiplos recebimentos com as caixas de seleção para vinculá-los a um único PIX.
            </p>
          )}
        </div>
      )}

      {/* barra flutuante de seleção múltipla */}
      <BarraSelecaoMultipla
        selecionados={selecionados}
        onVincular={() => setModalPixGrupo(true)}
        onLimpar={() => setSelecionados([])}
      />

      {/* modal de grupo */}
      {modalPixGrupo && (
        <ModalPixGrupo
          lancamentos={selecionados}
          dataCaixa={data}
          onClose={() => setModalPixGrupo(false)}
          onSalvar={salvarGrupo}
        />
      )}
    </div>
  );
}
